import React, { useState, useEffect } from "react";
import styled from "@emotion/styled";
import { spacing } from "@mui/system";
import {
  Alert as MuiAlert,
  Box,
  IconButton,
  InputBase,
  Button,
  Card as MuiCard,
  CardContent,
  CircularProgress,
  FormControl as MuiFormControl,
  Typography as MuiTypography,
  Grid,
  Paper as MuiPaper,
  Select,
  MenuItem,
  InputLabel,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Toolbar,
  TablePagination,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { settingsMenu } from "../../../../../redux/slices/settings";
import { useDispatch, useSelector } from "react-redux";
import { isValidToken } from "../../../../../utils/jwt";
import {
  fetchLogOut,
  fetchLoginLogOut_Socket,
  logout,
} from "../../../../../redux/slices/login";
import { useNavigate } from "react-router-dom";
import { fetchAgentsList } from "../../../../../redux/slices/agents";
import { fetchLocationList } from "../../../../../redux/slices/locationSettings";
import {
  mapAgentToLocation,
  fetchAgentLocationMappings,
  hardDeleteAgentLocationMapping,
} from "../../../../../redux/slices/agentLocationMapping";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import toast from "react-hot-toast";

const Alert = styled(MuiAlert)(spacing);
const Search = styled.div`
  margin-top: 5px;
  margin-left: 5px;
  margin-right: 5px;
  background-color: #e7f2ff;
`;
const Typography = styled(MuiTypography)(spacing);
const Paper = styled(MuiPaper)(spacing);
const Card = styled(MuiCard)(spacing);

const FormControl = styled(MuiFormControl)(spacing);

const StyledCard = styled(Card)(({ theme }) => ({
  margin: theme.spacing(2),
  borderRadius: theme.spacing(2),
  boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
}));

const LocationSettingsAgentMapping = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const accessToken = window.localStorage.getItem("accessToken");
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [page, setPage] = useState(0);
  const [search, setSearch] = React.useState("");

  let rows = [];

  const fetchAgentsListData = useSelector(
    (state) => state.agents.fetchAgentsListData
  );

  const fetchAgentLocationMappingsData = useSelector(
    (state) => state.agentLocationMapping.fetchAgentLocationMappingsData
  );
  const fetchLocationListData = useSelector(
    (state) => state.locationSettings.fetchLocationListData
  );

  const [selectedAgents, setSelectedAgents] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [error, setError] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [mapAgentData, setMapAgentData] = useState([]);
  const [agentsData, setAgentsData] = useState([]);
  const [locationsData, setLocationsData] = useState([]);

  useEffect(() => {
    dispatch(fetchLocationList());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchAgentsList());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchAgentLocationMappings());
  }, [dispatch]);

  useEffect(() => {
    if (
      fetchAgentLocationMappingsData?.status === "succeeded" &&
      fetchAgentLocationMappingsData?.data
    ) {
      const mappingsData = fetchAgentLocationMappingsData.data.data;
      setMapAgentData(mappingsData);
    }
  }, [fetchAgentLocationMappingsData]);

  // location List
  useEffect(() => {
    if (
      fetchLocationListData?.status === "succeeded" &&
      fetchLocationListData?.data
    ) {
      setLocationsData(fetchLocationListData.data);
    }
  }, [fetchLocationListData?.status, fetchLocationListData?.data]);

  // Agent List
  useEffect(() => {
    if (
      fetchAgentsListData?.status === "succeeded" &&
      fetchAgentsListData?.data
    ) {
      setAgentsData(fetchAgentsListData.data);
    }
  }, [fetchAgentsListData?.status, fetchAgentsListData?.data]);

  const getMappedAgentIdsForSelectedLocation = (locationId) => {
    if (!locationId || !mapAgentData) return [];

    // Find all mappings for this location
    const locationMappings = mapAgentData.filter(
      (mapping) => String(mapping.sticky_location_id) === String(locationId)
    );

    // Extract all agent IDs from all mappings
    const agentIds = [];

    locationMappings.forEach((mapping) => {
      // Handle both response formats
      if (Array.isArray(mapping.agents)) {
        mapping.agents.forEach((agent) => {
          if (agent?.agent_id) agentIds.push(String(agent.agent_id));
        });
      }
      if (Array.isArray(mapping.agent_id)) {
        mapping.agent_id.forEach((id) => agentIds.push(String(id)));
      }
    });

    return [...new Set(agentIds)]; // Return unique IDs
  };

  useEffect(() => {
    if (selectedLocation && mapAgentData) {
      // Get agent IDs mapped to the selected location
      const mappedAgentIds =
        getMappedAgentIdsForSelectedLocation(selectedLocation);

      // Set them as selected
      setSelectedAgents(mappedAgentIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation, mapAgentData]);

  const handleBack = () => {
    if (!isValidToken(accessToken)) {
      dispatch(fetchLogOut());
      dispatch(fetchLoginLogOut_Socket());
      dispatch(logout());
      navigate("/");
    } else {
      dispatch(
        settingsMenu({
          list: "locationsettings",
          listData: [],
        })
      );
    }
  };

  const handleHardDelete = async (mappingId, agentId) => {
    try {
      setIsProcessing(true);
      await dispatch(
        hardDeleteAgentLocationMapping({
          mappingId: String(mappingId),
          agentId: String(agentId),
        })
      ).unwrap();
      toast.success("Agent removed successfully");
      dispatch(fetchAgentLocationMappings()); // Add this line to update the mapAgentData state
    } catch (err) {
      toast.error(err.message || "Failed to remove agent");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedLocation) {
      setError("Please select a location");
      return;
    }

    try {
      setIsProcessing(true);

      // Get current and new agent IDs
      const currentAgentIds =
        getMappedAgentIdsForSelectedLocation(selectedLocation);
      const newAgentIds = selectedAgents.map((agentId) => String(agentId));

      if (newAgentIds.length === 0) {
        toast.error("Please select at least one valid agent");
        return;
      }

      // Determine agents to add and remove
      const agentsToAdd = newAgentIds.filter(
        (id) => !currentAgentIds.includes(id)
      );
      const agentsToRemove = currentAgentIds.filter(
        (id) => !newAgentIds.includes(id)
      );

      // Process removals
      if (agentsToRemove.length > 0) {
        const locationMappings = mapAgentData.filter(
          (mapping) =>
            String(mapping.sticky_location_id) === String(selectedLocation)
        );

        for (const agentId of agentsToRemove) {
          const mapping = locationMappings.find(
            (mapping) =>
              mapping.agents?.some(
                (agent) => String(agent.agent_id) === agentId
              ) || mapping.agent_id?.includes(agentId)
          );

          if (mapping) {
            await dispatch(
              hardDeleteAgentLocationMapping({
                mappingId: String(mapping._id),
                agentId: String(agentId),
              })
            ).unwrap();
          }
        }
      }

      // Process additions
      if (agentsToAdd.length > 0) {
        await dispatch(
          mapAgentToLocation({
            agent_ids: agentsToAdd,
            location_id: String(selectedLocation),
          })
        ).unwrap();
      }

      // Refresh the mappings data
      dispatch(fetchAgentLocationMappings()).unwrap();

      toast.success("Agent mappings updated successfully");
    } catch (err) {
      toast.error(err.message || "Failed to update agent mappings");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const searchFilter = (e) => {
    setPage(0);
    setSearch(e.target.value);
  };

  // rows =  search.trim() !=='' ?  mapAgentData.filter((data) => mapAgentData.includes(search.trim())) : mapAgentData;

  const handleChange = (event) => {
    const {
      target: { value },
    } = event;
    setSelectedAgents(
      // On autofill we get a the stringified value.
      typeof value === "string" ? value.split(",") : value
    );
  };

  const handleLocationChange = (event) => {
    const newLocationId = event.target.value;
    setSelectedLocation(newLocationId);

    // Don't set selectedAgents here - let the useEffect handle it
  };

  const mappedDataWithNames = Array.isArray(mapAgentData)
    ? mapAgentData.map((mapping) => {
        const location = locationsData.find(
          (loc) =>
            String(loc._id) ===
            String(
              mapping.sticky_location_id?.$oid || mapping.sticky_location_id
            )
        );

        let agentsForDisplay = [];
        const agentIds = mapping.agents
          ? mapping.agents.map((agent) => agent.agent_id)
          : [];

        agentsForDisplay = agentIds
          .map((agentId) => {
            const agent = agentsData.find(
              (a) => String(a._id) === String(agentId)
            );
            return agent
              ? {
                  agent_id: agent._id,
                  displayname: agent.name || agent.email || "Unknown Agent",
                }
              : null;
          })
          .filter(Boolean);

        return {
          _id: mapping._id,
          location_name: location?.location_name || "Unknown Location",
          agents: agentsForDisplay,
        };
      })
    : [];

  rows =
    search.trim() !== ""
      ? mappedDataWithNames.filter((row) => {
          if (!row) return false;
          const locationMatch = row.location_name
            ?.toLowerCase()
            .includes(search.toLowerCase());
          // const agentMatch = row.agents?.some(agent =>
          //   agent?.displayname?.toLowerCase().includes(search.toLowerCase())
          // )
          return locationMatch;
        })
      : mappedDataWithNames;

  if (fetchAgentLocationMappingsData?.status === "loading") {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (
    fetchAgentsListData?.status === "error" ||
    fetchLocationListData?.status === "error"
  ) {
    return (
      <Box>
        <Alert severity="error">
          Failed to load agents or locations data. Please try again.
        </Alert>
      </Box>
    );
  }

  if (!agentsData || !locationsData) {
    return (
      <Box>
        <Alert severity="info">No agents or locations available.</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Paper>
        <Toolbar>
          <Typography variant="h6" id="tableTitle">
            Agent - Location Mapping
          </Typography>
          <Box flexGrow={1} />
          <div>
            <Button
              mr={2}
              variant="contained"
              color="primary"
              style={{ marginRight: "5px" }}
              onClick={handleBack}
            >
              <NavigateBeforeIcon />
              Back
            </Button>
          </div>
        </Toolbar>
      </Paper>
      <StyledCard>
        <CardContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Select Location</InputLabel>
                <Select
                  value={selectedLocation}
                  onChange={handleLocationChange}
                  label="Select Location"
                >
                  {locationsData && locationsData.length > 0 ? (
                    locationsData.map((location) => (
                      <MenuItem key={location._id} value={String(location._id)}>
                        {location.location_name}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>No locations available</MenuItem>
                  )}
                </Select>
                {/* <Select
                  value={selectedLocation}
                  onChange={handleLocationChange}
                  label="Select Location"
                  disabled={fetchLocationListData?.status === 'loading'}
                >
                  {fetchLocationListData?.status === 'loading' ? (
                    <MenuItem disabled>
                      <CircularProgress size={24} />
                    </MenuItem>
                  ) : locationsData.length > 0 ? (
                    locationsData.map(location => (
                      <MenuItem key={location._id} value={String(location._id)}>
                        {location.location_name}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>No locations available</MenuItem>
                  )}
                </Select> */}
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Select Agents</InputLabel>
                <Select
                  multiple
                  value={selectedAgents}
                  onChange={handleChange}
                  label="Select Agents"
                  disabled={isProcessing}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((value) => {
                        const agent = agentsData.find(
                          (a) => String(a._id) === String(value)
                        );
                        return (
                          <Chip
                            key={value}
                            label={
                              agent?.name || agent?.email || "Unknown Agent"
                            }
                            color="primary"
                            onDelete={() => {
                              setSelectedAgents(
                                selectedAgents.filter((id) => id !== value)
                              );
                            }}
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  {agentsData.map((agent) => {
                    const isMapped = getMappedAgentIdsForSelectedLocation(
                      selectedLocation
                    ).includes(String(agent._id));

                    return (
                      <MenuItem key={agent._id} value={String(agent._id)}>
                        {agent.name}
                        {isMapped && (
                          <span
                            style={{
                              marginLeft: "8px",
                              color: "#666",
                              fontSize: "0.8rem",
                            }}
                          >
                            (currently mapped)
                          </span>
                        )}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={
              !selectedLocation ||
              isProcessing ||
              fetchAgentsListData?.status === "loading"
            }
            fullWidth
          >
            {isProcessing ? (
              <CircularProgress size={24} />
            ) : (
              "Map Selected Agents"
            )}
          </Button>
          <Box mt={4}>
            <Typography variant="h6">Agent Location Mapping</Typography>
            <Search>
              <IconButton type="button" sx={{ p: "4px" }} aria-label="search">
                <SearchIcon />
              </IconButton>
              <InputBase
                sx={{ ml: 1, flex: 1 }}
                placeholder="Search"
                onChange={searchFilter}
              />
            </Search>
            <Paper sx={{ width: "100%", overflow: "hidden", mt: 2 }}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <strong>Location</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Agents</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.length > 0 ? (
                      rows
                        .slice(
                          page * rowsPerPage,
                          page * rowsPerPage + rowsPerPage
                        )
                        .map((row, index) => (
                          <TableRow key={index}>
                            <TableCell>{row.location_name}</TableCell>
                            <TableCell>
                              {Array.isArray(row.agents) &&
                                row.agents.map((agent, idx) => (
                                  <Chip
                                    key={idx}
                                    label={agent.displayname}
                                    onDelete={() =>
                                      handleHardDelete(
                                        row?._id,
                                        agent?.agent_id
                                      )
                                    }
                                    sx={{
                                      marginRight: "5px",
                                      marginBottom: "5px",
                                    }}
                                  />
                                ))}
                            </TableCell>
                          </TableRow>
                        ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={2} align="center">
                          No mappings found.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={rows.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Paper>
          </Box>
        </CardContent>
      </StyledCard>
    </Box>
  );
};

export default LocationSettingsAgentMapping;
